import { Users, BarChart3, Activity, Shield, Home, FileText, Inbox, Bell, LineChart, ScrollText, Settings, MessageCircle, Building2, Crown } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useSuperuser } from "@/hooks/useSuperuser";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarTrigger,
    useSidebar,
} from "@/components/ui/sidebar";

const adminItems = [
    { title: "Dashboard", url: "/admin", icon: Home },
    { title: "User Management", url: "/admin/users", icon: Users },
    { title: "Properties", url: "/admin/properties", icon: Building2 },
    { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
    { title: "Reports", url: "/admin/reports", icon: FileText },
    { title: "Placement", url: "/admin/claims", icon: Activity },
    { title: "Messages", url: "/admin/messages", icon: MessageCircle },
];

const notificationItems = [
    { title: "Broadcasts", url: "/admin/broadcasts", icon: Bell },
    { title: "Notification Analytics", url: "/admin/notification-analytics", icon: LineChart },
];

const systemItems = [
    { title: "System Logs", url: "/admin/logs", icon: ScrollText },
    { title: "Log Retention", url: "/admin/log-retention", icon: Settings },
    { title: "System Settings", url: "/admin/settings", icon: Crown, superuserOnly: true },
];

const intakeItems = [
    { title: "WP Property Submissions", url: "/admin/wp/properties", icon: Inbox },
    { title: "WP Claim Submissions", url: "/admin/wp/claims", icon: Inbox },
];

export function AdminSidebar() {
    const { state } = useSidebar();
    const location = useLocation();
    const currentPath = location.pathname;
    const collapsed = state === "collapsed";
    const { unreadCount } = useUnreadMessages({ isAdmin: true });
    const { isSuperuser } = useSuperuser();

    const isActive = (path: string) => {
        if (path === "/admin") {
            return currentPath === path;
        }
        return currentPath.startsWith(path);
    };

    const isExpanded = adminItems.some((i) => isActive(i.url));

    return (
        <Sidebar
            collapsible="icon"
            className="border-r"
        >
            <SidebarContent>
                <div className="flex items-center gap-2 px-4 py-6">
                    <Shield className="h-6 w-6 text-primary" />
                    {!collapsed && (
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-lg font-bold">Admin Portal</h2>
                                {isSuperuser && (
                                    <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                                        <Crown className="h-3 w-3 mr-1" />
                                        Super
                                    </Badge>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">TPS Management</p>
                        </div>
                    )}
                </div>

                <SidebarGroup>
                    <SidebarGroupLabel>Administration</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {adminItems.map((item) => (
                                <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                                        <NavLink
                                            to={item.url}
                                            end={item.url === "/admin"}
                                            className="hover:bg-accent"
                                            activeClassName="bg-accent text-accent-foreground font-medium"
                                        >
                                            <item.icon className="h-4 w-4" />
                                            {!collapsed && <span>{item.title}</span>}
                                            {item.title === "Messages" && unreadCount > 0 && (
                                                <Badge variant="destructive" className="ml-auto text-xs px-1.5 py-0">
                                                    {unreadCount > 9 ? "9+" : unreadCount}
                                                </Badge>
                                            )}
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <SidebarGroup>
                    <SidebarGroupLabel>Notifications</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {notificationItems.map((item) => (
                                <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                                        <NavLink
                                            to={item.url}
                                            className="hover:bg-accent"
                                            activeClassName="bg-accent text-accent-foreground font-medium"
                                        >
                                            <item.icon className="h-4 w-4" />
                                            {!collapsed && <span>{item.title}</span>}
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <SidebarGroup>
                    <SidebarGroupLabel>Intake</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {intakeItems.map((item) => (
                                <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                                        <NavLink
                                            to={item.url}
                                            className="hover:bg-accent"
                                            activeClassName="bg-accent text-accent-foreground font-medium"
                                        >
                                            <item.icon className="h-4 w-4" />
                                            {!collapsed && <span>{item.title}</span>}
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <SidebarGroup>
                    <SidebarGroupLabel>System</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {systemItems.filter(item => !('superuserOnly' in item && item.superuserOnly) || isSuperuser).map((item) => (
                                <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                                        <NavLink
                                            to={item.url}
                                            className="hover:bg-accent"
                                            activeClassName="bg-accent text-accent-foreground font-medium"
                                        >
                                            <item.icon className="h-4 w-4" />
                                            {!collapsed && <span>{item.title}</span>}
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <div className="mt-auto p-4">
                    <SidebarMenuButton asChild>
                        <NavLink to="/" className="hover:bg-accent">
                            <Home className="h-4 w-4" />
                            {!collapsed && <span>Back to Site</span>}
                        </NavLink>
                    </SidebarMenuButton>
                </div>
            </SidebarContent>
        </Sidebar>
    );
}